import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Rag } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import OverviewTab from "./case/OverviewTab";
import TimelineTab from "./case/TimelineTab";
import DocumentsTab from "./case/DocumentsTab";
import ComplianceTab from "./case/ComplianceTab";
import CaseIQTab from "./case/CaseIQTab";
import IssuesTab from "./case/IssuesTab";
import DirectionsTab from "./case/DirectionsTab";
import AdjournmentsTab from "./case/AdjournmentsTab";
import AdrTab from "./case/AdrTab";
import ConclaveTab from "./case/ConclaveTab";
import CostsTab from "./case/CostsTab";
import NextStepsTab from "./case/NextStepsTab";
import DecisionTab from "./case/DecisionTab";
import IntakeTab from "./case/IntakeTab";
import { CaseNotificationsTab, CaseAuditTab } from "./case/ActivityTabs";

const TABS = [
  ["next", "What Happens Next"], ["lodgement", "Lodgement"], ["overview", "Overview"], ["timeline", "Timeline"], ["documents", "Documents"], ["compliance", "Compliance"], ["caseiq", "CaseIQ"], ["issues", "Issues & Chronology"],
  ["directions", "Directions"], ["adjournments", "Adjournments"], ["adr", "ADR"], ["conclave", "Conclave"], ["costs", "Costs"], ["decision", "Decision"], ["notifications", "Notifications"], ["audit", "Audit"],
];

export default function CaseDetail() {
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const { can, selectPracticeArea, user } = useAuth();
  const [c, setC] = useState(null);
  const tab = sp.get("tab") || (["applicant", "respondent"].includes(user?.role) ? "next" : "overview");

  const reload = useCallback(() => api.get(`/cases/${id}`).then((r) => { setC(r.data); selectPracticeArea(r.data.practice_area); }).catch((e) => toast.error(errMsg(e))), [id]); // eslint-disable-line
  useEffect(() => { reload(); }, [reload]);

  if (!c) return <div className="page text-sm text-slate-500">Loading case…</div>;
  const tabs = TABS.filter(([k]) => ((k !== "audit" && k !== "notifications") || can("audit.view", "case.view_all")) && (k !== "decision" || can("caseiq.member_brief")));

  return (
    <div className="page space-y-5" data-testid="case-detail-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="back-to-dashboard"><ArrowLeft className="h-3 w-3" /> Dashboard</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3"><span className="mono text-sm text-blue-900 font-semibold" data-testid="case-number">{c.case_number}</span><Rag level={c.triage} label={`${c.triage.toUpperCase()} lane`} testId="case-triage" /><span className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-0.5" data-testid="case-practice-area">{c.practice_area_name} · v{c.config_version}</span></div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1" data-testid="case-title">{c.title}</h1>
          <div className="text-sm text-slate-600 mt-1">{c.state_label} · Claim {money(c.claim_amount)}{c.counterclaim_amount ? ` · Counterclaim ${money(c.counterclaim_amount)}` : ""} · {c.property_address}</div>
        </div>
        <div className="panel px-5 py-3 flex items-center gap-6">
          <div><div className="eyebrow">Readiness</div><div className="text-2xl font-bold mono" data-testid="case-readiness">{c.readiness}%</div></div>
          <div><div className="eyebrow">Overdue</div><div className={`text-2xl font-bold mono ${c.overdue_count ? "text-rose-700" : ""}`} data-testid="case-overdue">{c.overdue_count}</div></div>
          <div><div className="eyebrow">Pending approvals</div><div className="text-2xl font-bold mono" data-testid="case-pending">{c.pending_approvals}</div></div>
        </div>
      </div>
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })}>
        <TabsList className="flex flex-wrap h-auto justify-start bg-white border border-slate-200 p-1">
          {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`tab-${k}`} className="data-[state=active]:bg-blue-900 data-[state=active]:text-white text-xs">{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="next"><NextStepsTab c={c} /></TabsContent>
        <TabsContent value="lodgement"><IntakeTab c={c} reload={reload} /></TabsContent>
        <TabsContent value="decision"><DecisionTab c={c} /></TabsContent>
        <TabsContent value="overview"><OverviewTab c={c} reload={reload} /></TabsContent>
        <TabsContent value="timeline"><TimelineTab c={c} reload={reload} /></TabsContent>
        <TabsContent value="documents"><DocumentsTab c={c} reload={reload} /></TabsContent>
        <TabsContent value="compliance"><ComplianceTab c={c} reload={reload} /></TabsContent>
        <TabsContent value="caseiq"><CaseIQTab c={c} /></TabsContent>
        <TabsContent value="issues"><IssuesTab c={c} /></TabsContent>
        <TabsContent value="directions"><DirectionsTab c={c} reload={reload} /></TabsContent>
        <TabsContent value="adjournments"><AdjournmentsTab c={c} reload={reload} /></TabsContent>
        <TabsContent value="adr"><AdrTab c={c} reload={reload} /></TabsContent>
        <TabsContent value="conclave"><ConclaveTab c={c} reload={reload} /></TabsContent>
        <TabsContent value="costs"><CostsTab c={c} reload={reload} /></TabsContent>
        <TabsContent value="notifications"><CaseNotificationsTab c={c} /></TabsContent>
        <TabsContent value="audit"><CaseAuditTab c={c} /></TabsContent>
      </Tabs>
    </div>
  );
}
